import type { CatalogRatioCreateInput, CatalogRatioUpdateInput } from 'gql/graphql';
import { RatioTypeEnum } from 'gql/graphql';
import type { DefaultItem } from 'uikit';
import { requiredBaseRule } from 'utils/constants';
import { replacerCost, replacerCostString } from 'utils/replacerCost';

import * as R from 'ramda';
import type { RegisterOptions } from 'react-hook-form';

import { RatiosTypeEnum } from '../CatalogRatios';
import type { FieldValuesRatioCreateOrEditStructure, TransformedValuesRatioCreateOrEditStructure } from '../types';

export const getCatalogRatioGroupIdRules = (
    ratioType?: DefaultItem['id'],
): Pick<RegisterOptions<FieldValuesRatioCreateOrEditStructure, 'catalogRatioGroupId'>, 'validate'> => ({
    validate: (value?: DefaultItem) => {
        if (ratioType === RatioTypeEnum.TechPartCoef) {
            return true;
        }
        if (!value) {
            return requiredBaseRule.required;
        }

        return true;
    },
});

export const validateJustification = (): Pick<
    RegisterOptions<FieldValuesRatioCreateOrEditStructure, 'justification'>,
    'validate'
> => ({
    validate: (value?: string) => {
        if (!value) {
            return requiredBaseRule.required;
        }

        if (!value?.trim()?.length) {
            return 'Поле не может состоять только из пробелов';
        }

        return true;
    },
});

export const getNumericRatioFieldRules = (options?: {
    isRequired?: boolean;
    allowZero?: boolean;
}): Pick<RegisterOptions<Record<string, string>>, 'validate'> => ({
    validate: (value?: string) => {
        const { isRequired = true, allowZero = false } = options || {};

        if (!value) {
            return isRequired ? requiredBaseRule.required : true;
        }

        const numericValue = replacerCost(value);
        const strValue = replacerCostString(value);
        const integerPart = strValue.split('.')[0];

        if (integerPart.length > 6) {
            return 'Длина целой части не более 6 знаков';
        }

        if (allowZero) {
            return numericValue >= 0 || 'Значение должно быть не меньше 0';
        }

        return numericValue > 0 || 'Значение должно быть больше 0';
    },
});

export const getRatioTypeLabel = (type: RatioTypeEnum) => `Коэффициент ${RatiosTypeEnum[type].toLowerCase()}`;

export const createCatalogRatioInput = (
    formData: TransformedValuesRatioCreateOrEditStructure,
    id?: number | null,
): CatalogRatioCreateInput | CatalogRatioUpdateInput => {
    const baseInput = {
        name: formData.name,
        kem: replacerCost(formData.kem),
        kmat: R.isNil(formData.kmat) ? null : replacerCost(formData.kmat),
        knr: replacerCost(formData.knr),
        kozp: replacerCost(formData.kozp),
        ksp: replacerCost(formData.ksp),
        ktz: replacerCost(formData.ktz),
        ktzzpm: replacerCost(formData.ktzzpm),
    };

    if (id) {
        return {
            ...baseInput,
            id,
            justification: formData.justification,
            ratioType: formData.ratioType.id,
        } as CatalogRatioUpdateInput;
    }

    return {
        ...baseInput,
        catalogRatioGroupId: formData.catalogRatioGroupId?.id,
        ratioType: formData.ratioType.id,
    } as CatalogRatioCreateInput;
};
